import React, { useState, useEffect } from "react";
import axios from "../../api/axiosConfig";
import "../../index.css";
import Swal from "sweetalert2";
import MainLayout from "../MainLayout";

// Función para obtener la fecha actual en México
const getMexicoCurrentDate = () => {
  // Importante: NO usar offsets fijos ni toISOString() (UTC) porque puede cambiar el día.
  // Esto devuelve YYYY-MM-DD en zona horaria de México.
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Mexico_City",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);

  const year = parts.find((p) => p.type === "year")?.value;
  const month = parts.find((p) => p.type === "month")?.value;
  const day = parts.find((p) => p.type === "day")?.value;

  return `${year}-${month}-${day}`;
};

const getWeekRangeFromMexicoISO = (mexicoISODate) => {
  const [y, m, d] = mexicoISODate.split("-").map((n) => parseInt(n, 10));
  const utc = new Date(Date.UTC(y, m - 1, d));
  const day = utc.getUTCDay(); // 0=Dom..6=Sab
  const diffToMonday = (day + 6) % 7; // Lun=0
  const monday = new Date(utc);
  monday.setUTCDate(utc.getUTCDate() - diffToMonday);
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  const weekStart = monday.toISOString().slice(0, 10);
  const weekEnd = sunday.toISOString().slice(0, 10);
  return { weekStart, weekEnd };
};

const getMonthRangeFromMexicoISO = (mexicoISODate) => {
  const year = parseInt(mexicoISODate.split("-")[0], 10);
  const month = parseInt(mexicoISODate.split("-")[1], 10);
  const first = new Date(Date.UTC(year, month - 1, 1));
  const last = new Date(Date.UTC(year, month, 0));
  return {
    monthStart: first.toISOString().slice(0, 10),
    monthEnd: last.toISOString().slice(0, 10),
  };
};

const getPeriodoRange = (periodo, mexicoISODate) => {
  const p = (periodo || "semanal").toLowerCase();
  if (p === "diario") {
    return { start: mexicoISODate, end: mexicoISODate, inicio: mexicoISODate, label: "Día" };
  }
  if (p === "mensual") {
    const { monthStart, monthEnd } = getMonthRangeFromMexicoISO(mexicoISODate);
    return { start: monthStart, end: monthEnd, inicio: monthStart, label: "Mes" };
  }
  const { weekStart, weekEnd } = getWeekRangeFromMexicoISO(mexicoISODate);
  return { start: weekStart, end: weekEnd, inicio: weekStart, label: "Semana" };
};

const DashboardCortesCaja = () => {
  const [periodo, setPeriodo] = useState("semanal");
  const [date, setDate] = useState(getMexicoCurrentDate());
  const { start, end, inicio, label } = getPeriodoRange(periodo, date);
  const [cortes, setCortes] = useState([]);
  const [cortesSucursales, setCortesSucursales] = useState([]);
  const [cortesIndividuales, setCortesIndividuales] = useState([]);
  const [total, setTotal] = useState(0);
  const [resumenTransacciones, setResumenTransacciones] = useState({
    sucursales: [],
    individuales: [],
  });
  const [totalResumen, setTotalResumen] = useState(0);

  useEffect(() => {
    const fetchCortes = async () => {
      try {
        const response = await axios.get(`/cortesCaja/admin/${inicio}?tipo=${periodo}`);
        console.log(response.data);
        const cortesData = response.data.data || [];
        setCortes(cortesData);

        // Calcular el total general sumando todos los importes
        const totalAmount = cortesData.reduce(
          (sum, transaction) => sum + transaction.totalGeneral,
          0
        );
        setTotal(totalAmount);

        // Sucursales filtrado
        setCortesSucursales(
          cortesData.filter((corte) => corte.tipoUsuarioCreador === 1)
        );

        // Todos los demas sin los de sucursal y ordenados por dirección de sucursal
        setCortesIndividuales(
          cortesData
            .filter((corte) => corte.tipoUsuarioCreador !== 1)
            .sort((a, b) =>
              a.direccionSucursal.localeCompare(b.direccionSucursal)
            )
        );

        // Si no hay cortes registrados, traer un resumen directo desde transacciones
        if (cortesData.length === 0) {
          const resumen = await axios.get(
            `/cortesCaja/resumenTransacciones/${inicio}?start=${start}&end=${end}`
          );
          const data = resumen.data?.data || { sucursales: [], individuales: [] };
          setResumenTransacciones(data);

          const totalFromResumen = (data.sucursales || []).reduce(
            (sum, row) => sum + Number(row.totalGeneral || 0),
            0
          );
          setTotalResumen(totalFromResumen);
        } else {
          setResumenTransacciones({ sucursales: [], individuales: [] });
          setTotalResumen(0);
        }
      } catch (error) {
        console.error("Error fetching transactions:", error);
      }
    };

    fetchCortes();
  }, [periodo, date, inicio, start, end]);

  return (
    <MainLayout>
      <div
        style={{
          margin: "0px 100px",
          display: "grid",
          justifyContent: "center",
        }}
      >
        <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ minWidth: "220px" }}>
            <label style={{ fontWeight: 700 }}>Periodo</label>
            <select
              className="form-control form-select"
              value={periodo}
              onChange={(e) => setPeriodo(e.target.value)}
              style={{ fontWeight: 700 }}
            >
              <option value="diario">Diario</option>
              <option value="semanal">Semanal</option>
              <option value="mensual">Mensual</option>
            </select>
          </div>
          <div>
            <label style={{ fontWeight: 700 }}>Fecha de referencia</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="form-control"
              style={{
                width: "fit-content",
                textAlign: "center",
                fontWeight: "700",
              }}
            />
          </div>
          <div style={{ fontWeight: "700" }}>
            {label}: <span style={{ fontWeight: 500 }}>{start} a {end}</span>
          </div>
        </div>
        <div style={{ display: "grid" }}>
          <div>
            <h4 style={{ marginTop: "50px" }}>
              <b>Cortes de Sucursales</b>
            </h4>
            <div style={{ maxHeight: "500px", overflowY: "auto" }}>
              <table
                className="table table-secondary"
                style={{ margin: "10px 0", fontSize: "13px" }}
              >
                <thead>
                  <tr>
                    <th scope="col">ID</th>
                    <th scope="col">Sucursal</th>
                    <th scope="col">Encargado</th>
                    <th scope="col">Total Efectivo</th>
                    <th scope="col">Total Credito</th>
                    <th scope="col">Total Debito</th>
                    <th scope="col">Total Transferencia</th>
                    <th scope="col">Total General</th>
                    <th scope="col" style={{ whiteSpace: "nowrap" }}>
                      Fecha Registro
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {cortesSucursales.map((transaction) => (
                    <tr key={transaction.idCorte}>
                      <td>{transaction.idCorte}</td>
                      <td style={{ fontSize: "12px" }}>
                        {transaction.direccionSucursal}
                      </td>
                      <td>{transaction.nombreCreador}</td>
                      <td>
                        {transaction.totalEfectivo.toLocaleString("es-MX", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td>
                        {transaction.totalCredito.toLocaleString("es-MX", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td>
                        {transaction.totalDebito.toLocaleString("es-MX", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td>
                        {transaction.totalTransferencia.toLocaleString("es-MX", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td>
                        {transaction.totalGeneral.toLocaleString("es-MX", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td>{transaction.fechaCorte.split("T")[0]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h4 style={{ marginTop: "50px" }}>
              <b>Cortes Individuales</b>
            </h4>
            <div style={{ maxHeight: "500px", overflowY: "auto" }}>
              <table
                className="table table-secondary"
                style={{ margin: "10px 0", fontSize: "13px" }}
              >
                <thead>
                  <tr>
                    <th scope="col">ID</th>
                    <th scope="col">Sucursal</th>
                    <th scope="col">Encargado</th>
                    <th scope="col">Total Efectivo</th>
                    <th scope="col">Total Credito</th>
                    <th scope="col">Total Debito</th>
                    <th scope="col">Total Transferencia</th>
                    <th scope="col">Total General</th>
                    <th scope="col" style={{ whiteSpace: "nowrap" }}>
                      Fecha Registro
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {cortesIndividuales.map((transaction) => (
                    <tr key={transaction.idCorte}>
                      <td>{transaction.idCorte}</td>
                      <td style={{ fontSize: "12px" }}>
                        {transaction.direccionSucursal}
                      </td>
                      <td>{transaction.nombreCreador}</td>
                      <td>
                        {transaction.totalEfectivo.toLocaleString("es-MX", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td>
                        {transaction.totalCredito.toLocaleString("es-MX", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td>
                        {transaction.totalDebito.toLocaleString("es-MX", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td>
                        {transaction.totalTransferencia.toLocaleString(
                          "es-MX",
                          { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                        )}
                      </td>
                      <td>
                        {transaction.totalGeneral.toLocaleString("es-MX", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td>{transaction.fechaCorte.split("T")[0]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        {cortes.length > 0 ? (
          <h3>
            Total del {label} Acumulado:{" "}
            <b>
              $
              {total.toLocaleString("es-MX", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </b>
          </h3>
        ) : (
          <>
            <h4 style={{ marginTop: "35px" }}>
              <b>Resumen de la semana (transacciones)</b>{" "}
              <span style={{ fontSize: "12px", fontWeight: 400 }}>
                (aún no hay cortes registrados)
              </span>
            </h4>

            <div style={{ maxHeight: "350px", overflowY: "auto" }}>
              <table
                className="table table-secondary"
                style={{ margin: "10px 0", fontSize: "13px" }}
              >
                <thead>
                  <tr>
                    <th scope="col">Sucursal</th>
                    <th scope="col">Efectivo</th>
                    <th scope="col">Crédito</th>
                    <th scope="col">Débito</th>
                    <th scope="col">Transferencia</th>
                    <th scope="col">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(resumenTransacciones.sucursales || []).map((row) => (
                    <tr key={`${row.idSucursal}-${row.direccionSucursal}`}>
                      <td style={{ fontSize: "12px" }}>{row.direccionSucursal}</td>
                      <td>{Number(row.totalEfectivo || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td>{Number(row.totalCredito || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td>{Number(row.totalDebito || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td>{Number(row.totalTransferencia || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td>{Number(row.totalGeneral || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3>
              Total del {label} (transacciones):{" "}
              <b>
                $
                {totalResumen.toLocaleString("es-MX", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </b>
            </h3>
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default DashboardCortesCaja;
